import { toast } from "react-toastify";
import {
  getApiErrorMessage,
  isNotFoundError,
  OdooApiError,
} from "app/utils/apiErrors";
import {
  AuthExpiredError,
  clearAuthData,
  isEmbeddedMode,
  recoverAuth,
} from "app/utils/auth";
import { BRANDING } from "app/utils/branding";

export type HandleApiErrorOptions = {
  embedded?: boolean;
  fallbackMessage?: string;
  autoClose?: number;
  silent?: boolean;
  onNotFound?: () => void;
  onSessionExpired?: () => void | Promise<void>;
};

export type HandleApiErrorResult = {
  recovered: boolean;
  shouldRetry: boolean;
};

export async function handleApiError(
  error: unknown,
  options: HandleApiErrorOptions = {}
): Promise<HandleApiErrorResult> {
  const embedded = options.embedded ?? isEmbeddedMode();
  const autoClose = options.autoClose ?? 6000;

  if (error instanceof AuthExpiredError) {
    const recovered = await recoverAuth();
    if (recovered?.access_token) {
      if (!options.silent) {
        toast.info("Session refreshed. Please retry.", { autoClose: 4000 });
      }
      return { recovered: true, shouldRetry: true };
    }

    if (!options.silent) {
      if (embedded) {
        toast.error(BRANDING.sessionExpiredRefreshPage, {
          autoClose: false,
        });
      } else {
        toast.error(BRANDING.sessionExpiredSignInAgain, {
          autoClose: 6000,
        });
        clearAuthData();
        window.location.assign("/login");
      }
    }

    await options.onSessionExpired?.();
    return { recovered: false, shouldRetry: false };
  }

  if (isNotFoundError(error)) {
    options.onNotFound?.();
  }

  if (!options.silent) {
    toast.error(
      getApiErrorMessage(error, options.fallbackMessage || "Request failed."),
      { autoClose }
    );
  }

  return { recovered: false, shouldRetry: false };
}

export function notifyApiError(
  error: unknown,
  fallbackMessage = "Request failed.",
  autoClose = 6000
): void {
  toast.error(getApiErrorMessage(error, fallbackMessage), { autoClose });
}

export function isMapConfigFallbackError(error: unknown): boolean {
  if (!(error instanceof OdooApiError)) {
    return false;
  }
  return (
    error.code === "no_active_provider" ||
    error.code === "model_not_available" ||
    error.code === "map_not_configured" ||
    error.code === "internal_server_error" ||
    error.httpStatus === 404 ||
    error.httpStatus === 500
  );
}
